import {
  Injectable,
  Inject,
  PLATFORM_ID,
  signal,
  computed,
  DestroyRef,
  inject,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { Router, NavigationStart } from '@angular/router';
import { filter } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { environment } from 'src/environments/environment';

export interface ErrorState {
  hasError: boolean;
  statusCode: number;
  message: string;
  statusText: string;
  devDetails?: {
    url?: string;
    error?: any;
    timestamp: Date;
  };
}

@Injectable({
  providedIn: 'root',
})
export class ErrorStateService {
  private destroyRef = inject(DestroyRef);
  private isBrowser: boolean;

  // Signal-based state management
  private errorSignal = signal<ErrorState | null>(null);

  // Public readonly signal for components to consume
  readonly error = this.errorSignal.asReadonly();

  // Computed signal for checking if there's an error
  readonly hasErrorSignal = computed(() => this.errorSignal() !== null);

  constructor(
    private router: Router,
    @Inject(PLATFORM_ID) private platformId: Object
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);

    // Clear error state on navigation
    this.router.events
      .pipe(
        filter((event) => event instanceof NavigationStart),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.clearError();
      });
  }

  /**
   * Set error state from HTTP error response
   */
  setError(
    statusCode: number,
    statusText: string,
    error?: any,
    url?: string
  ): void {
    const message = this.getErrorMessage(statusCode);

    const errorState: ErrorState = {
      hasError: true,
      statusCode,
      statusText,
      message,
    };

    // Include dev details only in development mode
    if ((environment as any).isDev) {
      errorState.devDetails = {
        url,
        error,
        timestamp: new Date(),
      };
    }

    this.errorSignal.set(errorState);
  }

  /**
   * Clear error state
   */
  clearError(): void {
    if (this.errorSignal() !== null) {
      this.errorSignal.set(null);
    }
  }

  /**
   * Check if there's currently an error
   */
  hasError(): boolean {
    return this.errorSignal() !== null;
  }

  /**
   * Get current error state synchronously
   */
  getCurrentError(): ErrorState | null {
    return this.errorSignal();
  }

  /**
   * Get user-friendly error message based on status code
   * Only handles: 400, 401, 403, 404, 500
   */
  private getErrorMessage(statusCode: number): string {
    switch (statusCode) {
      case 400:
        return 'error_inline.400_description';
      case 401:
        return 'error_inline.401_description';
      case 403:
        return 'error_inline.403_description';
      case 404:
        return 'error_inline.404_description';
      case 500:
        return 'error_inline.500_description';
      case 502:
      case 503:
      case 504:
        return 'error_inline.service_unavailable_description';
      default:
        return 'error_inline.description';
    }
  }
}
